import { useState, useMemo } from 'react';
import { checkPackType } from '../../../../../../generic/utils/checkPackType';
import { AvailablePacks, BasketProduct, BasketQuantities } from '../../../../../../generic/basket/types/basketTypes';
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages';

// Type definitions


interface BaseProduct {
    id: string;
    desc?: string;
    ProductDesc?: string;
    VarietyDesc?: string;
    ImageUrl?: string;
    PackID: number;
    Packs?: number;
    Price?: number;
    PackWeight?: number,
    ProductID: number,
    VarietyID: number,
    SizeID: string,
    Remark: string,
    OrderWeight?: number,
    ProductCalculationMethod: number,
    OrderPacks?: number,
    AvailablePacks?: AvailablePacks[]
}   

interface CreateModeProduct extends BaseProduct {
    Packs: number;
}

interface EditModeProduct extends BaseProduct {
    OrderPacks?: number;
    SalePrice?: number;
    Remarks?: string
}

interface EditModeProductNonOrder extends BaseProduct {
    Packs: number;
}




interface UseBasketParams {
    mode?: 'create' | 'edit';
    packegeList: IPackagesItem[];
    // For create mode
    product?: CreateModeProduct[];
    goodsListDefault?: BaseProduct[];
    // For edit mode  
    editGoods?: EditModeProduct[];
    productNonOrder?: EditModeProductNonOrder[];
    editGoodsDefault?: BaseProduct[];
}

interface UseBasketReturn {
    // State
    isBasketOpen: boolean;
    basketProducts: BasketProduct[];
    basketQuantities: BasketQuantities;
    
    // Handlers
    handleToggleBasket: () => void;
    handleOpenBasket: () => void;
    handleCloseBasket: () => void;
}

const useBasket = ({ 
    mode = 'create',
    packegeList,
    // For create mode
    product = [],
    goodsListDefault = [],
    // For edit mode  
    editGoods = [],
    productNonOrder = [],
    editGoodsDefault = []
}: UseBasketParams): UseBasketReturn => {
    const [isBasketOpen, setIsBasketOpen] = useState<boolean>(false);
    // Enhanced basket products with complete information
    const basketProducts = useMemo<BasketProduct[]>(() => {
        if (mode === 'create') {
            return product
                .filter((item: CreateModeProduct) => item.Packs > 0)
                .map((productItem: CreateModeProduct): BasketProduct => {
                    // Find matching item in goodsListDefault to get complete product info
                    const goodsItem = goodsListDefault.find((goods: BaseProduct) => goods.id === productItem.id);
                    const packType = checkPackType(packegeList, productItem.PackID);
                    
                    return {
                        ...productItem,
                        ProductDesc: goodsItem?.ProductDesc || productItem.desc || '',
                        VarietyDesc: goodsItem?.VarietyDesc || '',
                        ImageUrl: goodsItem?.ImageUrl || productItem.ImageUrl || '',
                        packType,
                        PackWeight: goodsItem?.PackWeight,
                        AvailablePacks: goodsItem?.AvailablePacks || []
                    };
                });
        }
        
        if (mode === 'edit') {
            // Create a map to merge existing orders with modifications
            const mergedProducts = new Map<string, BasketProduct>();
            
            // First, add all existing ordered items
            editGoods.forEach((item: EditModeProduct) => {
                if (item.OrderPacks && item.OrderPacks > 0) {
                    const packType = checkPackType(packegeList, item.PackID);
               
                    mergedProducts.set(item.id, {
                        ...item,
                        ProductDesc: item.ProductDesc || item.desc || '',
                        VarietyDesc: item.VarietyDesc || '',
                        ImageUrl: item.ImageUrl || '',
                        Packs: item.OrderPacks,
                        isExistingOrder: true,
                        packType,
                        Price: item.SalePrice || item.Price,
                        PackWeight: item.PackWeight,
                        AvailablePacks: item.AvailablePacks || []
                    });
                }
            });
            // Then, merge with new/modified items from productNonOrder
       
            productNonOrder.forEach((productItem: EditModeProductNonOrder) => {
                if (productItem.Packs > 0) {
                    const existingItem = mergedProducts.get(productItem.id);
                    const goodsItem = editGoodsDefault.find((goods: BaseProduct) => goods.id === productItem.id);
                    const packType = checkPackType(packegeList, productItem.PackID);
                    
                    mergedProducts.set(productItem.id, {
                        ...productItem,
                        ProductDesc: goodsItem?.ProductDesc || productItem.desc || '',
                        VarietyDesc: goodsItem?.VarietyDesc || '',
                        ImageUrl: goodsItem?.ImageUrl || productItem.ImageUrl || '',
                        Packs: productItem.Packs,
                        isExistingOrder: !!existingItem,
                        isModified: true,
                        packType,
                        Price: productItem.Price,
                        PackWeight: goodsItem?.PackWeight,
                        AvailablePacks: goodsItem?.AvailablePacks || []
                    });
                }
            });
            
            return Array.from(mergedProducts.values());
        }
        
        return [];
    }, [mode, product, goodsListDefault, editGoods, productNonOrder, editGoodsDefault, packegeList]);
    // Calculate quantities by pack type
    const basketQuantities = useMemo<BasketQuantities>(() => {
        const quantities: BasketQuantities = {
            weight: 0,     // PackType "1" - משקל
            units: 0,      // PackType "2" - יחידות  
            packages: 0,    // Others - חבילות
            totalWeight: 0 // Sum of all products weight exeption of packType "2"
        };
        
        basketProducts.forEach((productItem: BasketProduct) => {
            const packs = productItem.Packs || 0;
            const defaultProductArra = mode === 'create' ? goodsListDefault : editGoodsDefault;
            const defaultProduct = defaultProductArra.find((item: BaseProduct) => 
                item.ProductID === productItem.ProductID && 
                item.VarietyID === productItem.VarietyID && 
                item.SizeID.trim() === productItem.SizeID.trim()
            );

            // Calculate total weight for non-unit pack types
            const isWeightPackType = productItem.packType === '1'; // משקל
            const isUnitPackType = productItem.packType === '2';   // יחידות
            const isValidToCalculateTotalWeigh = defaultProduct && !isUnitPackType && !isWeightPackType;
            if (isValidToCalculateTotalWeigh) {
                const calculatePackWeight = (): number => {
                    // Use PackWeight if available
                    const tempPackWeight = defaultProduct?.AvailablePacks?.filter(e => e.PackID === productItem.PackID)[0]?.PackWeight
                
                    if (tempPackWeight || defaultProduct.PackWeight) {
                        return tempPackWeight || defaultProduct.PackWeight || 0;
                    }
                    
                    // Calculate weight per pack from OrderWeight and OrderPacks
                    const orderWeight = defaultProduct.OrderWeight || 0;
                    const orderPacks = defaultProduct.OrderPacks || 1;
                    return Number(orderWeight / orderPacks);
                };

                const packWeight = calculatePackWeight();
                quantities.totalWeight += packWeight * packs;
            } else {
                quantities.totalWeight +=  packs;
            }
            // Update pack type specific quantities
            if (isWeightPackType) {
                quantities.weight += packs;
            } else if (isUnitPackType) {
                quantities.units += packs;
            } else {
                quantities.packages += packs;
            }
        }); 
        
        return quantities;
    }, [basketProducts]);

    const handleToggleBasket = (): void => {
        setIsBasketOpen(!isBasketOpen);
    };

    const handleOpenBasket = (): void => {
        setIsBasketOpen(true);
    };

    const handleCloseBasket = (): void => {
        setIsBasketOpen(false);
    };

    return {
        // State
        isBasketOpen,
        basketProducts,
        basketQuantities,
        
        // Handlers
        handleToggleBasket,
        handleOpenBasket,
        handleCloseBasket
    };
};

export default useBasket; 